import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { logoUrl } from '../data';
import { signOutAdmin, verifyAdminAccess } from '../services/adminService';
import {
  getPaymentReviews
} from '../services/adminWorkspaceService';
import { getAccurateAdminMetrics } from '../services/adminOrderInsightsService';
import {
  getLandingImages,
  landingImageFields,
  updateLandingImages
} from '../services/landingService';
import AdminSidebar from '../admin/AdminSidebar';
import AdminOverview from '../admin/AdminOverview';
import AdminOrders from '../admin/AdminOrders';
import AdminInventory from '../admin/AdminInventory';
import AdminTryOn from '../admin/AdminTryOn';
import AdminNotifications from '../admin/AdminNotifications';
import AdminCustomersPanel from '../admin/AdminCustomersPanel';
import AdminReviewManagement from '../admin/AdminReviewManagement';
import AdminProductsList from '../admin/AdminProductsList';
import { adminNavGroups } from '../admin/adminModules';
import {
  AdminCategories,
  AdminPromotions
} from '../admin/AdminManagementModules';
import {
  AdminPaymentSettings,
  AdminShippingSettings
} from '../admin/AdminCommerceSettings';

const { FiMenu, FiMoon, FiSun, FiSave, FiSearch, FiBell } = FiIcons;

const THEME_KEY = 'zevi-admin-theme-v2';

function dateRange(period) {
  const end = new Date();
  const start = new Date();
  if (period === 'today') {
    start.setHours(0, 0, 0, 0);
  } else if (period === 'yesterday') {
    end.setHours(0, 0, 0, 0);
    start.setTime(end.getTime() - 86400000);
  } else if (period === 'month') {
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
  } else {
    start.setDate(start.getDate() - Number(period || 30));
  }
  return { start, end };
}

function LandingPageEditor() {
  const [values, setValues] = useState({});
  const [state, setState] = useState({
    loading: true,
    saving: false,
    message: '',
    error: ''
  });

  useEffect(() => {
    getLandingImages()
      .then((images) => {
        setValues(images);
        setState({
          loading: false,
          saving: false,
          message: '',
          error: ''
        });
      })
      .catch(() => {
        setState({
          loading: false,
          saving: false,
          message: '',
          error: 'Images could not be loaded.'
        });
      });
  }, []);

  const save = async (event) => {
    event.preventDefault();
    setState((current) => ({
      ...current,
      saving: true,
      error: '',
      message: ''
    }));
    try {
      await updateLandingImages(values);
      setState({
        loading: false,
        saving: false,
        error: '',
        message: 'Website photos updated!'
      });
    } catch (error) {
      setState({
        loading: false,
        saving: false,
        message: '',
        error: error.message
      });
    }
  };

  if (state.loading) {
    return <div className="admin-loading-state">Loading website images…</div>;
  }

  return (
    <div className="admin-overview-container">
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Website Banner Photos</h1>
          <p className="admin-page-subtitle">
            Update public photos on the storefront.
          </p>
        </div>
      </div>
      <form onSubmit={save}>
        {state.message && (
          <div className="admin-alert success">{state.message}</div>
        )}
        {state.error && (
          <div className="admin-alert error">{state.error}</div>
        )}
        <div className="admin-category-grid">
          {landingImageFields.map((field) => (
            <div className="admin-category-card" key={field.key}>
              <div className="landing-preview-box">
                {values[field.key] && (
                  <img src={values[field.key]} alt={field.label} />
                )}
              </div>
              <label className="admin-field-label">
                <strong>{field.label}</strong>
                <input
                  type="url"
                  required
                  value={values[field.key] || ''}
                  onChange={(event) =>
                    setValues((current) => ({
                      ...current,
                      [field.key]: event.target.value
                    }))
                  }
                />
              </label>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 20 }}>
          <button
            className="admin-btn-primary"
            type="submit"
            disabled={state.saving}
          >
            <SafeIcon icon={FiSave} />
            <span>{state.saving ? 'Saving…' : 'Save Photos'}</span>
          </button>
        </div>
      </form>
    </div>
  );
}

function AdminDashboardPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [menuOpen, setMenuOpen] = useState(false);
  const [period, setPeriod] = useState('30');
  const [metrics, setMetrics] = useState({});
  const [status, setStatus] = useState('checking');
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricsError, setMetricsError] = useState('');
  const [paymentBadge, setPaymentBadge] = useState(0);
  const [notificationCount, setNotificationCount] = useState(0);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const updateNotificationCount = useCallback((count) => setNotificationCount(count), []);
  const [theme, setTheme] = useState(
    () => localStorage.getItem(THEME_KEY) || 'light'
  );

  useEffect(() => {
    let active = true;
    async function verify() {
      const authorized = await verifyAdminAccess();
      if (!authorized) {
        navigate('/admin/login', { replace: true });
        return;
      }
      if (active) setStatus('ready');
    }
    verify();
    return () => {
      active = false;
    };
  }, [navigate]);

  useEffect(() => {
    if (status !== 'ready') return;
    const { start, end } = dateRange(period);
    setMetricsLoading(true);
    setMetricsError('');
    getAccurateAdminMetrics(start, end)
      .then(setMetrics)
      .catch((error) => setMetricsError(error.message))
      .finally(() => setMetricsLoading(false));

    getPaymentReviews()
      .then((rows) => setPaymentBadge(rows.length))
      .catch(() => setPaymentBadge(0));
  }, [period, status, tab]);

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    localStorage.setItem(THEME_KEY, next);
    setTheme(next);
  };

  const logout = async () => {
    await signOutAdmin();
    navigate('/admin/login', { replace: true });
  };

  if (status === 'checking') {
    return (
      <main className="admin-gate">
        <img
          src={logoUrl}
          alt="ZEVI"
          onError={(event) => {
            event.target.style.display = 'none';
          }}
        />
        <p>Verifying admin permissions…</p>
      </main>
    );
  }

  const modules = {
    overview: (
      metricsError
        ? <div className="admin-state admin-error">{metricsError}</div>
        : <AdminOverview
          metrics={metrics}
          loading={metricsLoading}
          onNavigate={(destination) =>
            setTab(destination === 'payments' ? 'orders' : destination)
          }
        />
    ),
    orders: <AdminOrders />,
    'try-on': <AdminTryOn />,
    products: <AdminProductsList />,
    categories: <AdminCategories />,
    inventory: <AdminInventory />,
    promotions: <AdminPromotions />,
    shipping: <AdminShippingSettings />,
    customers: <AdminCustomersPanel />,
    reviews: <AdminReviewManagement />,
    landing: <LandingPageEditor />,
    settings: <AdminPaymentSettings />
  };

  const tabLabel =
    adminNavGroups
      .flatMap((group) => group.items)
      .find((item) => item.id === tab)?.label ||
    tab.charAt(0).toUpperCase() + tab.slice(1);

  return (
    <main className={`admin-app-shell admin-theme-${theme}`}>
      <AdminSidebar
        active={tab}
        open={menuOpen}
        onChange={setTab}
        onClose={() => setMenuOpen(false)}
        onLogout={logout}
        paymentBadge={paymentBadge}
      />

      <div className="admin-main-viewport">
        <header className="admin-navbar">
          <div className="navbar-left">
            <button
              className="admin-menu-toggle-btn"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
            >
              <SafeIcon icon={FiMenu} />
            </button>
            <div className="admin-breadcrumb">
              <span className="crumb-root">Admin</span>
              <span className="crumb-sep">/</span>
              <span className="crumb-active">{tabLabel}</span>
            </div>
          </div>

          <div className="navbar-center">
            <div className="navbar-search-bar">
              <SafeIcon icon={FiSearch} />
              <input
                type="text"
                placeholder="Search orders, products, customers..."
                onFocus={() => {
                  if (tab !== 'orders' && tab !== 'products') setTab('orders');
                }}
              />
            </div>
          </div>

          <div className="navbar-right">
            {tab === 'overview' && (
              <select
                className="admin-select-pill"
                value={period}
                onChange={(event) => setPeriod(event.target.value)}
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="month">This month</option>
              </select>
            )}

            <button
              className="navbar-icon-btn"
              onClick={toggleTheme}
              title={`Switch to ${theme === 'dark' ? 'Light' : 'Dark'} mode`}
            >
              <SafeIcon icon={theme === 'dark' ? FiSun : FiMoon} />
            </button>

            <button
              className="navbar-icon-btn with-badge"
              onClick={() => setNotificationsOpen((current) => !current)}
              aria-label="Notifications"
              aria-expanded={notificationsOpen}
              title={`${notificationCount} item${notificationCount !== 1 ? 's' : ''} need attention`}
            >
              <SafeIcon icon={FiBell} />
              {notificationCount > 0 && (
                <span className="bell-badge">{notificationCount}</span>
              )}
            </button>
            {status === 'ready' && (
              <div style={{ display: notificationsOpen ? 'block' : 'none' }}>
                <AdminNotifications onCount={updateNotificationCount} onNavigate={(destination) => {
                  setTab(destination);
                  setNotificationsOpen(false);
                }} />
              </div>
            )}

            <div className="admin-user-profile">
              <div className="user-avatar">ZA</div>
              <div className="user-details">
                <strong>ZEVI Admin</strong>
                <small>Super Admin</small>
              </div>
            </div>
          </div>
        </header>

        <div className="admin-content-canvas">
          {modules[tab] || modules.overview}
        </div>
      </div>
    </main>
  );
}

export default AdminDashboardPage;